// ============================================
// Twitch Chat Tunnel Control Panel
// ============================================

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Radio, Send, Link2, Unlink, MessageSquare, Terminal } from 'lucide-react';
import { twitchClient } from '@/services/twitchTunnel';

export function TwitchTunnelPanel() {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [channel, setChannel] = useState(import.meta.env.VITE_TWITCH_CHANNEL || '');
  const [token, setToken] = useState(import.meta.env.VITE_TWITCH_TOKEN || '');
  const [nickname, setNickname] = useState('zen_user_' + Math.floor(Math.random() * 1000));
  const [messages, setMessages] = useState<string[]>([]);
  const [serverRoutes, setServerRoutes] = useState<string[]>([]);

  useEffect(() => {
    twitchClient.on('connected', () => {
      setConnected(true);
      setConnecting(false);
      toast.success('Connected to Twitch tunnel!');
    });

    twitchClient.on('disconnected', () => {
      setConnected(false);
      toast.info('Disconnected from Twitch');
    });

    twitchClient.on('message', (data: any) => {
      setMessages(prev => [...prev.slice(-9), JSON.stringify(data, null, 2)]);
    });

    twitchClient.on('serverReady', (routes: string[]) => {
      setServerRoutes(routes);
      toast.success('Server ready with routes: ' + routes.join(', '));
    });

    return () => {
      twitchClient.removeAllListeners();
    };
  }, []);

  const handleConnect = async () => {
    if (!channel || !token) {
      toast.error('Please enter channel and token');
      return;
    }

    setConnecting(true);
    try {
      // Update client credentials
      (twitchClient as any).channel = channel.toLowerCase().replace('#', '');
      (twitchClient as any).token = token;
      (twitchClient as any).nickname = nickname;

      await twitchClient.connect();
    } catch (error) {
      toast.error('Failed to connect: ' + (error as Error).message);
      setConnecting(false);
    }
  };

  const handleDisconnect = () => {
    twitchClient.disconnect();
    setConnected(false);
  };

  const handleTestRequest = async () => {
    try {
      const response = await twitchClient.get('/api/status');
      toast.success('Got response: ' + JSON.stringify(response.data));
    } catch (error) {
      toast.error('Request failed: ' + (error as Error).message);
    }
  };

  const handleSendCustom = async () => {
    const testData = {
      type: 'test',
      message: 'Hello from Zen-AI-Pentest!',
      timestamp: Date.now()
    };

    try {
      // Use API endpoint to send through backend tunnel
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      await fetch(`${API_URL}/twitch-tunnel/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testData)
      });
      toast.success('Test message sent!');
    } catch (error) {
      toast.error('Failed to send: ' + (error as Error).message);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-purple-500" />
          Twitch Chat Tunnel
          <Badge variant={connected ? "default" : "secondary"} className={connected ? 'bg-purple-500' : ''}>
            {connected ? 'Connected' : 'Disconnected'}
          </Badge>
        </CardTitle>
        <CardDescription>
          Tunnel your API through Twitch chat messages! 🎮
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {!connected ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="channel">Twitch Channel</Label>
              <Input
                id="channel"
                placeholder="your_channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The channel where the tunnel bot is listening
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="token">OAuth Token</Label>
              <Input
                id="token"
                type="password"
                placeholder="oauth:xxxxxxxxxxxxxxxxxxxxxx"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Get from: https://twitchtokengenerator.com/ (chat_login scope)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>

            <Button 
              onClick={handleConnect} 
              disabled={connecting}
              className="w-full"
            >
              {connecting ? (
                <>Connecting...</>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect to Twitch
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">Connected to #{channel}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleDisconnect}>
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>

            {serverRoutes.length > 0 && (
              <div className="space-y-2">
                <Label>Available Routes</Label>
                <div className="flex flex-wrap gap-2">
                  {serverRoutes.map(route => (
                    <Badge key={route} variant="outline" className="text-xs">
                      {route}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleTestRequest} variant="outline" className="flex-1">
                <Terminal className="w-4 h-4 mr-2" />
                Test Request
              </Button>
              <Button onClick={handleSendCustom} variant="outline" className="flex-1">
                <Send className="w-4 h-4 mr-2" />
                Send Test
              </Button>
            </div>

            {messages.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Last Messages
                </Label>
                <div className="bg-black text-green-400 p-3 rounded-lg font-mono text-xs max-h-48 overflow-y-auto">
                  {messages.map((msg, i) => (
                    <div key={i} className="mb-2 border-b border-green-800 pb-1">
                      <pre className="whitespace-pre-wrap">{msg}</pre>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-600">
                ⚠️ <strong>Warning:</strong> This sends data through public Twitch chat!
                Don't send sensitive information. For demo/hackathon use only.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
